// Bulk-email limits, safety thresholds and windows — shared by the API (audience rule, gates, worker,
// breaker, attribution) and the admin web app (Compose counters, schedule picker). Pure constants, no I/O.
import { AUTOMATION_MESSAGE_LIMITS } from './automation-journey';
import type { QuietHoursWindow } from './marketing-quiet-hours';

/** Recipients per Resend batch call (the provider's batch maximum). */
export const BULK_EMAIL_BATCH_SIZE = 100;

/** Anti-fatigue window: skip an address that got any marketing email (bulk OR automation) within this
 *  many hours. */
export const BULK_EMAIL_FATIGUE_WINDOW_HOURS = 72;

/** Salon-local send window for bulk email: quiet 9pm → 8am. Email has no TCPA quiet hours or state
 *  texting rules; this is courtesy + deliverability, so it never carries a `stateRule`. */
export const BULK_EMAIL_SEND_WINDOW: QuietHoursWindow = { startHour: 21, endHour: 8 };

/** Spam-complaint breaker: pause when complaints / delivered ≥ this rate, once at least
 *  {@link BULK_EMAIL_COMPLAINT_MIN_DELIVERED} delivered and {@link BULK_EMAIL_COMPLAINT_MIN_COUNT} complaints. */
export const BULK_EMAIL_COMPLAINT_RATE = 0.003;
export const BULK_EMAIL_COMPLAINT_MIN_DELIVERED = 200;
export const BULK_EMAIL_COMPLAINT_MIN_COUNT = 2;

/** Hard-bounce breaker: pause when hard bounces / sent ≥ this rate, once at least
 *  {@link BULK_EMAIL_HARD_BOUNCE_MIN_SENT} were sent. */
export const BULK_EMAIL_HARD_BOUNCE_RATE = 0.05;
export const BULK_EMAIL_HARD_BOUNCE_MIN_SENT = 100;

/** A booking counts for the email when it is made within this many days after the first click. */
export const BULK_EMAIL_CLICK_ATTRIBUTION_DAYS = 7;

/** Furthest an owner may schedule a bulk email ahead. */
export const BULK_EMAIL_SCHEDULE_MAX_DAYS = 30;

/** Daily caps used when `MARKETING_BULK_EMAIL_DAILY_CAP_PER_SALON` / `MARKETING_BULK_EMAIL_PLATFORM_DAILY_CAP`
 *  are unset (warm-up defaults for the new sending domain). */
export const DEFAULT_BULK_EMAIL_DAILY_CAP_PER_SALON = 1000;
export const DEFAULT_BULK_EMAIL_PLATFORM_DAILY_CAP = 10000;

/** Field caps — the Compose `maxLength` + counter and the API validator read the same numbers. */
export const BULK_EMAIL_SUBJECT_MAX = 150;
export const BULK_EMAIL_BODY_MAX = AUTOMATION_MESSAGE_LIMITS.emailBody;
export const BULK_EMAIL_BUTTON_LABEL_MAX = 30;

/** CASL implied consent (Canadian salons): a completed visit within this many months, or a lead
 *  sign-up (inquiry) within {@link CASL_IMPLIED_INQUIRY_MONTHS}. */
export const CASL_IMPLIED_VISIT_MONTHS = 24;
export const CASL_IMPLIED_INQUIRY_MONTHS = 6;
